import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/format";
import { Check, CheckCheck, Clock, X } from "lucide-react";
import { MessageMedia } from "./message-media";
import type { InboxMessage } from "@/types/inbox-message";

interface MessageBubbleProps {
  message: InboxMessage;
  isGrouped?: boolean;
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case "read":
      return <CheckCheck className="h-3 w-3 text-blue-400" />;
    case "sent":
      return <Check className="h-3 w-3" />;
    case "pending":
      return <Clock className="h-3 w-3" />;
    case "failed":
      return <X className="h-3 w-3 text-destructive" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
}

export function MessageBubble({ message, isGrouped }: MessageBubbleProps) {
  const isOutbound = message.direction === "outbound";
  const isSystem = message.source === "system";

  if (isSystem) {
    return (
      <div className="flex justify-center py-1">
        <span className="rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground">
          {message.content}
        </span>
      </div>
    );
  }

  if (message.is_private) {
    return (
      <div className={cn("flex", isOutbound ? "justify-end" : "justify-start")}>
        <div
          className={cn(
            "max-w-[70%] rounded-2xl px-3.5 py-2 shadow-sm",
            "border-l-2 border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
            isGrouped && "mt-0.5",
          )}
        >
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider opacity-60">
            Nota privada
          </span>

          {message.content_type !== "text" && message.content_url && (
            <MessageMedia
              contentType={message.content_type}
              contentUrl={message.content_url}
            />
          )}

          {message.content && (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {message.content}
            </p>
          )}

          <div className="mt-1 flex items-center gap-1.5 text-[10px] opacity-50">
            <span>{formatTime(message.datetime_add)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex",
        isOutbound ? "justify-end" : "justify-start",
        isGrouped ? "mt-0.5" : "mt-2",
      )}
    >
      <div
        className={cn(
          "max-w-[70%] rounded-2xl px-3.5 py-2 shadow-sm",
          isOutbound
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {message.content_type !== "text" && message.content_url && (
          <MessageMedia
            contentType={message.content_type}
            contentUrl={message.content_url}
          />
        )}

        {message.content && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </p>
        )}

        <div
          className={cn(
            "mt-1 flex items-center gap-1.5 text-[10px]",
            isOutbound ? "justify-end opacity-70" : "opacity-50",
          )}
        >
          <span>{formatTime(message.datetime_add)}</span>
          {isOutbound && <StatusIcon status={message.status} />}
        </div>
      </div>
    </div>
  );
}
